import { viewRuntime } from "./runtime.js";
import { viewStyle } from "./style.js";

/** One view as one self-contained HTML document: inline style and script, nothing fetched. `body` is static markup. */
export function viewDocument(title: string, body: string, script: string): string {
  return `<!doctype html><html lang="en"><head><meta charset="utf-8"><meta name="viewport" content="width=device-width,initial-scale=1"><title>${title}</title><style>${viewStyle}</style></head><body>${body}<script>${viewRuntime}\n${script}</script></body></html>`;
}

/** The patient/encounter line every view opens with. Runs in the page: `patient` and `encounter` come from the payload. */
export const identityScript = `
function identityHtml(patient, encounter, note) {
  return '<div class="who"><div><h1>' + view.esc(patient.name) + '</h1><span class="meta">MRN ' + view.esc(patient.patient_id) +
    ' · DOB ' + view.esc(patient.dob) + '</span></div><div class="meta">Encounter ' + view.esc(encounter.id) +
    ' · revision ' + view.esc(encounter.revision) + (encounter.signed ? ' · <span class="badge warn">Signed</span>' : '') +
    (note ? '<br>' + view.esc(note) : '') + '</div></div>';
}
function showStatus(root, text, isError) {
  var el = root.querySelector('.status');
  if (!el) { el = document.createElement('div'); root.appendChild(el); }
  el.className = 'status' + (isError ? ' error' : '');
  el.setAttribute('role', isError ? 'alert' : 'status');
  el.textContent = text;
  view.reportSize();
}
function explain(failure) {
  if (failure.code === 'stale_revision') return 'The chart changed since this was proposed. Nothing was written; ask Dobby to propose it again.';
  if (failure.code === 'encounter_signed') return 'This encounter is signed, so it can no longer change. Nothing was written.';
  return failure.message + ' Nothing was written.';
}
/** Read one of the app's own tools; rejects on failure, so a chain of reads stops at the first that fails. */
function read(name, args) {
  return view.callTool(name, args).then(function (r) { if (!r.ok || !r.data) throw r; return r.data; });
}
/**
 * The store refused the write as stale: something else changed the chart after this view opened (careline-sibling-
 * review-confirms-conflict-on-revision: every write moves the revision, so a sibling view's write is enough). The
 * write is never retried for the clinician; each one is theirs, and Talkie asks them again. reread resolves to
 * { state, revision, signed } as of now; shown renders what the clinician decides on, so comparing it before and
 * after tells "the chart moved on" from "this changed"; adopt takes the fresh state and re-renders. If a read
 * fails the view stays closed and says to ask again.
 */
function afterStale(root, verb, before, reread, shown, adopt) {
  showStatus(root, 'The chart changed since this opened. Checking what changed…', true);
  reread().then(function (now) {
    // A signed encounter can take no write, so the view stays locked on what it showed.
    if (now.signed) { showStatus(root, explain({ code: 'encounter_signed' }), true); return; }
    var same = shown(before) === shown(now.state);
    adopt(now.state);
    if (same) showStatus(root, 'The chart moved to revision ' + now.revision + ', but this has not changed. Nothing was written; ' + verb + ' again.');
    else showStatus(root, 'This changed since you opened it. Nothing was written; review it as it is now before you ' + verb + '.', true);
  }, function () { showStatus(root, explain({ code: 'stale_revision' }), true); });
}
`;
